import React, { useState, useEffect } from 'react';
import { Lock, Upload, FileText, Trash2, CheckCircle, AlertCircle, Calendar, LogOut, HardDrive, Database, FolderOpen, BarChart3, KeyRound, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AnimateIn } from '../components/Layout';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import AnalyticsDashboard from '../components/AnalyticsDashboard';
import { categoryStructure, determineLocation, getLocationOptions, impactLevels } from '../data/categoryData';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const indianStates = [
  "Central",
  // States (28)
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  // Union Territories (8)
  "Andaman & Nicobar Islands", "Chandigarh", "Dadra & Nagar Haveli and Daman & Diu",
  "Delhi", "Jammu & Kashmir", "Ladakh", "Lakshadweep", "Puducherry",
];

const categories = [
  "ESI", "PF", "Labour Code", "Minimum Wages", "Professional Tax",
  "Registers", "Returns", "Gazette Notifications", "POSH", "Leave", "N&F Holidays",
];

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authToken, setAuthToken] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [resources, setResources] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [storageStats, setStorageStats] = useState(null);
  const [activeTab, setActiveTab] = useState('upload'); // 'upload' or 'analytics'
  
  // New cascading dropdown states
  const [category, setCategory] = useState('');
  const [subCategory1, setSubCategory1] = useState('');
  const [subCategory2, setSubCategory2] = useState('');
  const [locationType, setLocationType] = useState('');
  const [specificLocation, setSpecificLocation] = useState('');
  
  // Other form fields
  const [formData, setFormData] = useState({
    title: '', notification_date: '', effective_date: '',
    short_summary: '', key_changes: '', 
    action_required: '',
  });
  const [pdfFile, setPdfFile] = useState(null);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const { toast } = useToast();

  // Change password modal state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);

  // Cascading dropdown logic
  useEffect(() => {
    // When category changes, reset all subcategories
    setSubCategory1('');
    setSubCategory2('');
    setLocationType('');
    setSpecificLocation('');
  }, [category]);

  useEffect(() => {
    // When sub_category_1 changes, reset sub_category_2 and determine location
    setSubCategory2('');
    if (category && subCategory1) {
      const locationInfo = determineLocation(category, subCategory1, '');
      setLocationType(locationInfo.type);
    }
  }, [category, subCategory1]);

  useEffect(() => {
    // When sub_category_2 changes, determine final location
    if (category) {
      const locationInfo = determineLocation(category, subCategory1, subCategory2);
      setLocationType(locationInfo.type);
      // Store the full location info for later use
      if (window.currentLocationInfo) {
        window.currentLocationInfo = locationInfo;
      } else {
        window.currentLocationInfo = locationInfo;
      }
    }
  }, [category, subCategory1, subCategory2]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, {
        email: emailInput.trim(),
        password: passwordInput,
      });
      const token = res.data.token;
      setAuthToken(token);
      setIsAuthenticated(true);
      sessionStorage.setItem('admin_token', token);
    } catch (err) {
      setLoginError(err?.response?.data?.detail || 'Invalid email or password.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setAuthToken('');
    sessionStorage.removeItem('admin_token');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast({ title: 'Please fill all password fields', variant: 'destructive' });
      return;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast({ title: 'New passwords do not match', variant: 'destructive' });
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      toast({ title: 'New password must be at least 6 characters', variant: 'destructive' });
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(`${API}/admin/change-password`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      }, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      toast({ title: 'Password changed successfully!' });
      setShowChangePassword(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      if (err?.response?.status === 400) {
        toast({ title: 'Current password is incorrect', variant: 'destructive' });
      } else if (err?.response?.status === 401) {
        toast({ title: 'Session expired. Please login again.', variant: 'destructive' });
        handleLogout();
      } else {
        toast({ title: 'Failed to change password', variant: 'destructive' });
      }
    } finally {
      setChangingPassword(false);
    }
  };

  useEffect(() => {
    const savedToken = sessionStorage.getItem('admin_token');
    if (savedToken) {
      setAuthToken(savedToken);
      setIsAuthenticated(true);
    }
  }, []);

  const fetchResources = async () => {
    try {
      const res = await axios.get(`${API}/resources`);
      setResources(res.data);
    } catch (err) {
      console.error('Failed to fetch resources', err);
    }
  };

  const fetchStorageStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/storage-stats`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setStorageStats(res.data);
    } catch (err) {
      console.error('Failed to fetch storage stats', err);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchResources();
      fetchStorageStats();
    }
  }, [isAuthenticated]);



  // Generate AI Summary from PDF
  const handleGenerateSummary = async () => {
    if (!pdfFile) {
      alert('The Resource is not attached. Please upload a PDF first.');
      return;
    }

    if (!authToken) {
      alert('Authentication required. Please login again.');
      return;
    }

    setGeneratingSummary(true);
    try {
      const formDataForSummary = new FormData();
      formDataForSummary.append('pdf', pdfFile);
      
      console.log('Sending request to:', `${API}/admin/generate-summary`);
      console.log('Token:', authToken ? 'Present' : 'Missing');
      
      const response = await axios.post(`${API}/admin/generate-summary`, formDataForSummary, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${authToken}`
        },
      });
      
      console.log('Summary response:', response.data);
      
      setFormData(prev => ({
        ...prev,
        short_summary: response.data.summary
      }));
      
      alert('Summary generated successfully! You can edit it before submitting.');
    } catch (error) {
      console.error('Summary generation error:', error);
      console.error('Error response:', error.response?.data);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to generate summary';
      alert(`Error: ${errorMsg}`);
    } finally {
      setGeneratingSummary(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    // Validation for new cascading fields
    if (!formData.title || !formData.notification_date || !formData.effective_date || !category) {
      toast({ title: 'Please fill all required fields', variant: 'destructive' });
      return;
    }
    if (!pdfFile) {
      toast({ title: 'Please select a PDF file', variant: 'destructive' });
      return;
    }

    setUploading(true);
    try {
      const data = new FormData();
      data.append('title', formData.title);
      data.append('notification_date', formData.notification_date);
      data.append('effective_date', formData.effective_date);
      data.append('applicability', 'N/A');  // Backend still expects it, send default
      
      // New cascading fields
      data.append('category', category);
      data.append('sub_category_1', subCategory1 || '');
      data.append('sub_category_2', subCategory2 || '');
      data.append('location_type', locationType || '');
      data.append('specific_location', specificLocation || '');
      
      data.append('short_summary', formData.short_summary || '');
      data.append('key_changes', formData.key_changes || '');
      data.append('action_required', formData.action_required || '');
      data.append('pdf', pdfFile);

      await axios.post(`${API}/resources`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${authToken}`,
        },
      });

      toast({ title: 'Resource uploaded successfully!' });
      
      // Reset all fields
      setFormData({ 
        title: '', notification_date: '', effective_date: '', 
        short_summary: '', key_changes: '', 
        who_applies_to: '', impact_level: '', action_required: '' 
      });
      setCategory('');
      setSubCategory1('');
      setSubCategory2('');
      setLocationType('');
      setSpecificLocation('');
      setPdfFile(null);
      const fileInput = document.getElementById('pdf-upload');
      if (fileInput) fileInput.value = '';
      fetchResources();
      fetchStorageStats(); // Refresh storage stats after upload
    } catch (err) {
      if (err?.response?.status === 401) {
        toast({ title: 'Session expired. Please login again.', variant: 'destructive' });
        handleLogout();
      } else {
        toast({ title: 'Upload failed', description: err?.response?.data?.detail || 'Please try again.', variant: 'destructive' });
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (resourceId) => {
    if (!window.confirm('Are you sure you want to delete this resource?')) return;
    try {
      await axios.delete(`${API}/resources/${resourceId}`, {
        headers: { 'Authorization': `Bearer ${authToken}` },
      });
      toast({ title: 'Resource deleted' });
      fetchResources();
      fetchStorageStats(); // Refresh storage stats after delete
    } catch (err) {
      if (err?.response?.status === 401) {
        toast({ title: 'Session expired. Please login again.', variant: 'destructive' });
        handleLogout();
      } else {
        toast({ title: 'Delete failed', variant: 'destructive' });
      }
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try { return new Date(dateStr).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' }); }
    catch { return dateStr; }
  };

  // Login Gate
  if (!isAuthenticated) {
    return (
      <div className="pt-20 min-h-screen bg-[#F8F9FA]">
        <Toaster />
        <div className="flex items-center justify-center py-32">
          <AnimateIn>
            <div className="bg-white p-10 rounded-sm shadow-lg max-w-md w-full mx-6">
              <div className="w-14 h-14 rounded-full bg-[#0B1F3A] flex items-center justify-center mx-auto mb-6">
                <Lock size={24} className="text-[#C9A646]" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-[#0B1F3A] text-center mb-2">Admin Access</h2>
              <p className="text-gray-500 font-body text-sm text-center mb-8">
                Enter your admin credentials to access the resource management panel.
              </p>
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="text-[#0B1F3A] font-body font-semibold text-sm mb-1.5 block">Email</label>
                  <Input
                    type="email"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    placeholder="Admin email address"
                    className="h-12 rounded-sm border-gray-200 font-body"
                  />
                </div>
                <div>
                  <label className="text-[#0B1F3A] font-body font-semibold text-sm mb-1.5 block">Password</label>
                  <Input
                    type="password"
                    value={passwordInput}
                    onChange={(e) => setPasswordInput(e.target.value)}
                    placeholder="Enter password"
                    className="h-12 rounded-sm border-gray-200 font-body"
                  />
                </div>
                {loginError && (
                  <p className="text-red-500 font-body text-sm flex items-center gap-2">
                    <AlertCircle size={14} /> {loginError}
                  </p>
                )}
                <Button type="submit" disabled={loginLoading} className="bg-[#C9A646] hover:bg-[#B8953B] text-[#0B1F3A] font-semibold font-body rounded-sm w-full h-12 disabled:opacity-50">
                  {loginLoading ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>
            </div>
          </AnimateIn>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="pt-20 min-h-screen bg-[#F8F9FA]">
      <Toaster />
      {/* Admin Header */}
      <section className="py-10 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <span className="text-[#C9A646] font-body font-semibold text-sm tracking-[0.25em] uppercase mb-2 block">Admin Panel</span>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#0B1F3A]">Resource Management</h1>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setShowChangePassword(true)}
              className="border-[#C9A646] text-[#C9A646] hover:bg-[#C9A646] hover:text-white font-body rounded-sm h-10 px-5">
              <KeyRound size={16} className="mr-2" /> Change Password
            </Button>
            <Button variant="outline" onClick={handleLogout}
              className="border-gray-200 text-gray-500 hover:text-red-500 hover:border-red-200 font-body rounded-sm h-10 px-5">
              <LogOut size={16} className="mr-2" /> Logout
            </Button>
          </div>
        </div>
      </section>

      {/* Change Password Modal */}
      {showChangePassword && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-[#0B1F3A] font-heading">Change Password</h2>
              <button onClick={() => setShowChangePassword(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                <X size={20} className="text-gray-400" />
              </button>
            </div>
            
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="text-[#0B1F3A] font-body font-semibold text-sm mb-1.5 block">Current Password *</label>
                <Input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                  placeholder="Enter current password"
                  className="h-11 rounded-sm border-gray-200 font-body"
                  required
                />
              </div>
              
              <div>
                <label className="text-[#0B1F3A] font-body font-semibold text-sm mb-1.5 block">New Password *</label>
                <Input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                  placeholder="Enter new password (min 6 characters)"
                  className="h-11 rounded-sm border-gray-200 font-body"
                  required
                />
              </div>
              
              <div>
                <label className="text-[#0B1F3A] font-body font-semibold text-sm mb-1.5 block">Confirm New Password *</label>
                <Input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  placeholder="Re-enter new password"
                  className="h-11 rounded-sm border-gray-200 font-body"
                  required
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button
                  type="submit"
                  disabled={changingPassword}
                  className="flex-1 bg-[#0B1F3A] hover:bg-[#0B1F3A]/90 text-white font-body rounded-sm h-11"
                >
                  {changingPassword ? 'Changing...' : 'Change Password'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowChangePassword(false);
                    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  }}
                  className="flex-1 border-gray-200 text-gray-500 hover:text-[#0B1F3A] font-body rounded-sm h-11"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Storage Monitoring */}
      {storageStats && (
        <section className="py-8 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Disk Storage */}
              <AnimateIn delay={0}>
                <div className="bg-white p-6 rounded-lg shadow-sm border border-blue-100">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <HardDrive size={20} className="text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-body font-semibold text-sm text-gray-600">Disk Storage</h3>
                        <p className="text-xs text-gray-400">Total capacity</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold text-[#0B1F3A]">{storageStats.disk.free_gb} GB</span>
                      <span className="text-sm text-gray-500">free</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${storageStats.disk.percent_used > 80 ? 'bg-red-500' : storageStats.disk.percent_used > 60 ? 'bg-yellow-500' : 'bg-green-500'}`}
                        style={{ width: `${storageStats.disk.percent_used}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500">
                      {storageStats.disk.used_gb} GB used of {storageStats.disk.total_gb} GB ({storageStats.disk.percent_used}%)
                    </p>
                  </div>
                </div>
              </AnimateIn>

              {/* PDF Files */}
              <AnimateIn delay={100}>
                <div className="bg-white p-6 rounded-lg shadow-sm border border-purple-100">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                        <FolderOpen size={20} className="text-purple-600" />
                      </div>
                      <div>
                        <h3 className="font-body font-semibold text-sm text-gray-600">PDF Files</h3>
                        <p className="text-xs text-gray-400">Uploaded documents</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold text-[#0B1F3A]">{storageStats.uploads.file_count}</span>
                      <span className="text-sm text-gray-500">files</span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Total: {storageStats.uploads.total_size_mb} MB
                    </p>
                    <p className="text-xs text-gray-500">
                      Avg size: {storageStats.uploads.avg_size_mb} MB per file
                    </p>
                  </div>
                </div>
              </AnimateIn>

              {/* Database */}
              <AnimateIn delay={200}>
                <div className="bg-white p-6 rounded-lg shadow-sm border border-green-100">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                        <Database size={20} className="text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-body font-semibold text-sm text-gray-600">Database</h3>
                        <p className="text-xs text-gray-400">MongoDB metadata</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold text-[#0B1F3A]">{storageStats.database.resource_count}</span>
                      <span className="text-sm text-gray-500">resources</span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Data: {storageStats.database.data_size_mb} MB
                    </p>
                    <p className="text-xs text-gray-500">
                      Storage: {storageStats.database.storage_size_mb} MB
                    </p>
                  </div>
                </div>
              </AnimateIn>
            </div>
          </div>
        </section>
      )}

      {/* Tab Navigation */}
      <section className="bg-white border-b border-gray-200 sticky top-20 z-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('upload')}
              className={`flex items-center gap-2 px-6 py-4 font-body font-semibold text-sm transition-all border-b-2 ${
                activeTab === 'upload'
                  ? 'border-[#C9A646] text-[#0B1F3A]'
                  : 'border-transparent text-gray-500 hover:text-[#0B1F3A]'
              }`}
            >
              <Upload size={18} />
              Upload & Manage
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`flex items-center gap-2 px-6 py-4 font-body font-semibold text-sm transition-all border-b-2 ${
                activeTab === 'analytics'
                  ? 'border-[#C9A646] text-[#0B1F3A]'
                  : 'border-transparent text-gray-500 hover:text-[#0B1F3A]'
              }`}
            >
              <BarChart3 size={18} />
              Analytics & Reports
            </button>
          </div>
        </div>
      </section>

      {/* Tab Content */}
      {activeTab === 'upload' ? (
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Upload Form */}
          <div className="lg:col-span-2">
            <AnimateIn>
              <div className="bg-white p-8 rounded-sm shadow-sm border border-gray-100 sticky top-28">
                <h2 className="font-heading text-xl font-bold text-[#0B1F3A] mb-6 flex items-center gap-2">
                  <Upload size={20} className="text-[#C9A646]" /> Upload Resource
                </h2>
                <form onSubmit={handleUpload} className="space-y-5">
                  <div>
                    <label className="text-[#0B1F3A] font-body font-semibold text-sm mb-1.5 block">Title *</label>
                    <Input name="title" value={formData.title} onChange={handleChange} placeholder="e.g., Minimum Wages Revision — Tamil Nadu"
                      className="h-11 rounded-sm border-gray-200 font-body" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-[#0B1F3A] font-body font-semibold text-sm mb-1.5 block">Notification Date *</label>
                      <Input name="notification_date" type="date" value={formData.notification_date} onChange={handleChange}
                        className="h-11 rounded-sm border-gray-200 font-body" />
                    </div>
                    <div>
                      <label className="text-[#0B1F3A] font-body font-semibold text-sm mb-1.5 block">Effective Date *</label>
                      <Input name="effective_date" type="date" value={formData.effective_date} onChange={handleChange}
                        className="h-11 rounded-sm border-gray-200 font-body" />
                    </div>
                  </div>
                  
                  {/* Category Section */}
                  
                  {/* New Cascading Dropdowns */}
                  <div>
                    <label className="text-[#0B1F3A] font-body font-semibold text-sm mb-1.5 block">Category *</label>
                    <Select value={category} onValueChange={setCategory}>
                      <SelectTrigger className="h-11 rounded-sm border-gray-200 font-body">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.keys(categoryStructure).map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Sub-Category 1 (conditional) */}
                  {category && categoryStructure[category]?.subCategories1?.length > 0 && (
                    <div>
                      <label className="text-[#0B1F3A] font-body font-semibold text-sm mb-1.5 block">Sub-Category *</label>
                      <Select value={subCategory1} onValueChange={setSubCategory1}>
                        <SelectTrigger className="h-11 rounded-sm border-gray-200 font-body">
                          <SelectValue placeholder="Select sub-category" />
                        </SelectTrigger>
                        <SelectContent>
                          {categoryStructure[category].subCategories1.map(sub => (
                            <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Sub-Category 2 (conditional) */}
                  {category && subCategory1 && categoryStructure[category]?.subCategories2?.[subCategory1] && (
                    <div>
                      <label className="text-[#0B1F3A] font-body font-semibold text-sm mb-1.5 block">Type *</label>
                      <Select value={subCategory2} onValueChange={setSubCategory2}>
                        <SelectTrigger className="h-11 rounded-sm border-gray-200 font-body">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          {categoryStructure[category].subCategories2[subCategory1].map(sub2 => (
                            <SelectItem key={sub2} value={sub2}>{sub2}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Location dropdown (conditional based on location type) */}
                  {category && locationType && locationType !== 'All India' && (
                    <div>
                      <label className="text-[#0B1F3A] font-body font-semibold text-sm mb-1.5 block">
                        Location {locationType && `(${locationType})`} *
                      </label>
                      <Select value={specificLocation} onValueChange={setSpecificLocation}>
                        <SelectTrigger className="h-11 rounded-sm border-gray-200 font-body">
                          <SelectValue placeholder={`Select ${locationType.toLowerCase()}`} />
                        </SelectTrigger>
                        <SelectContent className="max-h-60">
                          {getLocationOptions(locationType, window.currentLocationInfo || null).map(loc => (
                            <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Show location type info for All India */}
                  {category && locationType === 'All India' && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-sm text-blue-900 font-body">
                        📍 This category applies to <strong>All India</strong>
                      </p>
                    </div>
                  )}

                  <div className="border-t border-gray-100 pt-5 mt-1">
                    <p className="text-[#C9A646] font-body font-semibold text-xs tracking-wider uppercase mb-4">Resource Details</p>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-[#0B1F3A] font-body font-semibold text-sm">Short Summary</label>
                      <Button
                        type="button"
                        onClick={handleGenerateSummary}
                        disabled={generatingSummary || !pdfFile}
                        className="h-8 px-3 text-xs bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        {generatingSummary ? 'Generating...' : '✨ Generate with AI'}
                      </Button>
                    </div>
                    <Textarea name="short_summary" value={formData.short_summary} onChange={handleChange}
                      placeholder="Brief overview of this notification (or click Generate with AI)..."
                      className="min-h-[80px] rounded-sm border-gray-200 font-body text-sm" />
                  </div>
                  <div>
                    <label className="text-[#0B1F3A] font-body font-semibold text-sm mb-1.5 block">Key Changes</label>
                    <Textarea name="key_changes" value={formData.key_changes} onChange={handleChange}
                      placeholder="List the key changes introduced..."
                      className="min-h-[80px] rounded-sm border-gray-200 font-body text-sm" />
                  </div>
                  <div>
                    <label className="text-[#0B1F3A] font-body font-semibold text-sm mb-1.5 block">Action Required</label>
                    <Textarea name="action_required" value={formData.action_required} onChange={handleChange}
                      placeholder="What steps should the employer take..."
                      className="min-h-[80px] rounded-sm border-gray-200 font-body text-sm" />
                  </div>
                  <div>
                    <label className="text-[#0B1F3A] font-body font-semibold text-sm mb-1.5 block">PDF Upload *</label>
                    <input
                      id="pdf-upload"
                      type="file"
                      accept=".pdf"
                      onChange={(e) => setPdfFile(e.target.files[0] || null)}
                      className="w-full h-11 px-3 py-2 rounded-sm border border-gray-200 font-body text-sm text-gray-600 file:mr-4 file:py-1 file:px-4 file:rounded-sm file:border-0 file:bg-[#C9A646]/10 file:text-[#0B1F3A] file:font-semibold file:text-sm hover:file:bg-[#C9A646]/20 cursor-pointer"
                    />
                    {pdfFile && <p className="text-xs text-gray-400 font-body mt-1">{pdfFile.name}</p>}
                  </div>
                  <Button type="submit" disabled={uploading}
                    className="bg-[#C9A646] hover:bg-[#B8953B] text-[#0B1F3A] font-semibold font-body rounded-sm w-full h-12 disabled:opacity-50">
                    {uploading ? 'Uploading...' : 'Upload Resource'}
                  </Button>
                </form>
              </div>
            </AnimateIn>
          </div>

          {/* Existing Resources */}
          <div className="lg:col-span-3">
            <h2 className="font-heading text-xl font-bold text-[#0B1F3A] mb-6">Uploaded Resources ({resources.length})</h2>
            {resources.length === 0 ? (
              <div className="bg-white p-12 rounded-sm text-center border border-gray-100">
                <FileText size={40} className="text-gray-200 mx-auto mb-4" />
                <p className="text-gray-400 font-body">No resources uploaded yet. Use the form to add your first resource.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {resources.map((res) => (
                  <div key={res.id} className="bg-white border border-gray-100 rounded-sm p-5 hover:shadow-sm transition-shadow">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-heading font-bold text-[#0B1F3A] text-base truncate">{res.title}</h4>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs font-body text-gray-400">
                          <span>Notified: {formatDate(res.notification_date)}</span>
                          <span>Effective: {formatDate(res.effective_date)}</span>
                          {res.state && <span>{res.state}</span>}
                        </div>
                        <div className="flex gap-2 mt-2">
                          <span className="px-2 py-0.5 rounded-sm bg-[#0B1F3A]/5 text-[#0B1F3A] font-body text-xs">{res.act_law_name}</span>
                          <span className="px-2 py-0.5 rounded-sm bg-[#C9A646]/10 text-[#C9A646] font-body text-xs">{res.applicability}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <a href={`${BACKEND_URL}${res.pdf_url}`} target="_blank" rel="noopener noreferrer">
                          <Button variant="outline" size="sm" className="border-gray-200 text-[#0B1F3A] hover:bg-[#0B1F3A] hover:text-white rounded-sm h-8 px-3 text-xs">
                            <FileText size={14} className="mr-1" /> View
                          </Button>
                        </a>
                        <Button variant="outline" size="sm" onClick={() => handleDelete(res.id)}
                          className="border-gray-200 text-red-400 hover:bg-red-50 hover:text-red-600 hover:border-red-200 rounded-sm h-8 px-3 text-xs">
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      ) : (
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
          <AnalyticsDashboard authToken={authToken} />
        </div>
      )}
    </div>
  );
};

export default Admin;
